import React, { useState, useEffect, useRef } from "react";
import { useDataLayer } from "../../../Contexts/DataLayer.context";
import { min } from "date-fns";
import { formatLocalYMD, getTodayEasternYMD, parseYMDDate } from "../../../utils/dateUtils.ts";

const FBXCalendar = ({
    dateIn = null,
    placeholder = "Select a date",
    onDateChange,
    style,
    minYear,
    maxYear,
    fieldName,
    isOpen,
    onToggleCalendar,
    emptyDefaultYearOffset = 0,
}) => {
    const { savedDate, updateSavedDate } = useDataLayer();

    const [readyToSet, setReadyToSet] = useState(false);
    const [openDirection, setOpenDirection] = useState("down");
    const [view, setView] = useState("Calendar"); // "Calendar" | "Year" | "Month"
    const [calendarDays, setCalendarDays] = useState([]);
    const [possibleYears, setPossibleYears] = useState([]);

    const calendarRef = useRef(null);
    const selectedYearRef = useRef(null);
    const [popupWidth, setPopupWidth] = useState(null);

    const today = parseYMDDate(getTodayEasternYMD()) || new Date();
    const fallbackDate = new Date(today);
    fallbackDate.setFullYear(today.getFullYear() + Number(emptyDefaultYearOffset || 0));

    const normalizedDateIn =
        dateIn instanceof Date && !isNaN(dateIn) ? dateIn : null;

    const [selectedDate, setSelectedDate] = useState(
        normalizedDateIn ? formatLocalYMD(normalizedDateIn) : ""
    );
    const [selectedYear, setSelectedYear] = useState(
        normalizedDateIn ? normalizedDateIn.getFullYear() : fallbackDate.getFullYear()
    );
    const [selectedMonth, setSelectedMonth] = useState(
        normalizedDateIn ? normalizedDateIn.getMonth() : fallbackDate.getMonth()
    );

    const monthNames = [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
    ];

    const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

    // Rebuild calendar whenever month/year changes
    useEffect(() => {
        generateCalendar(selectedYear, selectedMonth);
    }, [selectedYear, selectedMonth]);

    // Keep internal date in sync with external dateIn
    useEffect(() => {
        if (!dateIn) {
            setSelectedDate("");
            setSelectedYear(fallbackDate.getFullYear());
            setSelectedMonth(fallbackDate.getMonth());
        } else {
            const normalized = dateIn instanceof Date ? dateIn : parseYMDDate(dateIn);
            if (normalized instanceof Date && !isNaN(normalized)) {
                const formatted = formatLocalYMD(normalized);
                setSelectedDate(formatted);
                setSelectedYear(normalized.getFullYear());
                setSelectedMonth(normalized.getMonth());
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dateIn]);

    const generateCalendar = (year, month) => {
        const startOfMonth = new Date(year, month, 1);
        const endOfMonth = new Date(year, month + 1, 0);
        const daysInMonth = endOfMonth.getDate();
        const startDay = startOfMonth.getDay();
        const paddedDays = Array.from({ length: startDay }, () => "");
        const realDays = Array.from({ length: daysInMonth }, (_, i) => i + 1);
        setCalendarDays([...paddedDays, ...realDays]);
    };

    const goToPreviousMonth = () => {
        if (selectedMonth === 0) {
            setSelectedMonth(11);
            setSelectedYear((prev) => prev - 1);
        } else {
            setSelectedMonth((prev) => prev - 1);
        }
    };

    const goToNextMonth = () => {
        if (selectedMonth === 11) {
            setSelectedMonth(0);
            setSelectedYear((prev) => prev + 1);
        } else {
            setSelectedMonth((prev) => prev + 1);
        }
    };

    const handleDayClick = (day) => {
        if (!day) return;

        const chosen = new Date(selectedYear, selectedMonth, day);
        const formatted = formatLocalYMD(chosen);

        if (readyToSet) {
            // Save into shared "saved date" bar if you use that feature
            updateSavedDate(formatted);
            setReadyToSet(false);
            return;
        }

        setSelectedDate(formatted);
        onDateChange?.(formatted);
        onToggleCalendar(false);
    };

    const resetDate = () => {
        setSelectedDate("");
        onDateChange?.(null);
        setReadyToSet(false);
        onToggleCalendar(false);
    };

    const setToday = () => {
        const formatted = getTodayEasternYMD();
        const now = parseYMDDate(formatted) || new Date();
        setSelectedYear(now.getFullYear());
        setSelectedMonth(now.getMonth());
        setSelectedDate(formatted);
        onDateChange?.(formatted);
        onToggleCalendar(false);
    };

    const parsedMinYear = parseInt(minYear || "1990", 10);
    const parsedMaxYear = parseInt(maxYear || "2030", 10);

    const switchToYearView = () => {
        const years = Array.from(
            { length: parsedMaxYear - parsedMinYear + 1 },
            (_, i) => parsedMinYear + i
        );
        setPossibleYears(years);
        setView("Year");
    };

    const selectYear = (year) => {
        setSelectedYear(year);
        setView("Calendar");
    };

    const selectMonth = (monthIndex) => {
        setSelectedMonth(monthIndex);
        setView("Calendar");
    };

    // Optional "save date" instruction logic (unused unless you wire it)
    const dismissBox = () => {
        const raw = localStorage.getItem("dismiss");
        const parsed = raw ? JSON.parse(raw) : [];
        if (!parsed.includes("saveDate")) {
            parsed.push("saveDate");
            localStorage.setItem("dismiss", JSON.stringify(parsed));
        }
        setView("Calendar");
    };

    const setCalender = () => {
        // if you ever want to support "save date bar" again
        if (!savedDate) {
            setReadyToSet(true);
        } else {
            const parsed = parseYMDDate(savedDate);
            if (parsed instanceof Date && !isNaN(parsed)) {
                const formatted = formatLocalYMD(parsed);
                setSelectedYear(parsed.getFullYear());
                setSelectedMonth(parsed.getMonth());
                setSelectedDate(formatted);
                onDateChange?.(formatted);
                setReadyToSet(false);
            }
        }
    };

    const toggleCalendar = () => {
        if (calendarRef.current) {
            const rect = calendarRef.current.getBoundingClientRect();
            const windowHeight = window.innerHeight;
            const spaceBelow = windowHeight - rect.bottom;
            const spaceAbove = rect.top;

            if (spaceBelow < 300 && spaceAbove > spaceBelow) {
                setOpenDirection("up");
            } else {
                setOpenDirection("down");
            }

            setPopupWidth(rect.width);
        }

        onToggleCalendar(fieldName);
    };

    // Scroll selected year into view when Year view opens
    useEffect(() => {
        if (view === "Year" && selectedYearRef.current) {
            selectedYearRef.current.scrollIntoView({
                block: "center",
                behavior: "auto",
            });
        }
    }, [view, possibleYears, selectedYear]);

    const selectedDay = selectedDate
        ? parseInt(selectedDate.split("-")[2], 10)
        : null;

    const popupStyle = {
        position: "absolute",
        left: 0,
        zIndex: 2050,
        minWidth: "350px",
        minHeight: "300px",
        top: openDirection === "down" ? "100%" : "auto",
        bottom: openDirection === "up" ? "100%" : "auto",
        width: popupWidth ? `${popupWidth}px` : "100%",
    };

    const isActive = !!selectedDate;
    const baseBorderColor = "#cdcbcbff";
    const activeBorderColor = "#446E9B";
    const borderColor = isActive ? activeBorderColor : baseBorderColor;

    return (
        <div
            className="fbx-calendar-container position-relative"
            style={style}
        >
            {/* Input + clear button (48-col friendly: just use w-100) */}


            <div
                ref={calendarRef}
                className="d-flex w-100"
                onClick={toggleCalendar}
                style={{
                    border: `2px solid ${borderColor}`,
                    borderRadius: "4px",
                    overflow: "hidden",        // clip inner corners so they look perfectly joined
                }}
            >
                <input
                    type="text"
                    readOnly
                    value={selectedDate || ""}
                    placeholder={selectedDate ? "" : placeholder}
                    className="form-control form-control-sm border-0 shadow-none"
                    style={{
                        borderRadius: 0,          // wrapper owns the radius
                        fontWeight: 400,
                        backgroundColor: isActive ? "black" : "white",
                        color: isActive ? "yellow" : "black",
                    }}
                    onClick={(e) => {
                        e.stopPropagation();
                        toggleCalendar();
                    }}
                />

                <button
                    type="button"
                    className="btn btn-sm border-0 shadow-none"
                    style={{
                        borderRadius: 0,          // wrapper owns the radius
                        minWidth: "32px",
                        backgroundColor: isActive ? "black" : "white",
                        color: isActive ? "yellow" : "transparent",
                    }}
                    disabled={!selectedDate}
                    onClick={(e) => {
                        e.stopPropagation();
                        resetDate();
                    }}
                >
                    X
                </button>
            </div>


            {/* MAIN CALENDAR VIEW */}
            {isOpen && view === "Calendar" && (
                <div
                    className="bg-white border rounded border-2 border-top-0 border-navy shadow-sm mt-0 p-2"
                    style={popupStyle}
                >
                    {/* Header: month / year / prev / next */}
                    <div className="d-flex justify-content-between align-items-center mb-2">
                        <button
                            type="button"
                            className="btn btn-sm btn-light px-3 fs-5"
                            onClick={goToPreviousMonth}
                        >
                            &laquo;
                        </button>

                        <div className="d-flex gap-3 text-center">
                            <button
                                type="button"
                                className="btn p-0 text-primary fs-7"
                                onClick={switchToYearView}
                            >
                                {selectedYear}
                            </button>
                            <button
                                type="button"
                                className="btn p-0 text-primary fs-7"
                                onClick={() => setView("Month")}
                            >
                                {monthNames[selectedMonth]}
                            </button>
                        </div>

                        <button
                            type="button"
                            className="btn btn-sm btn-light px-3 fs-5"
                            onClick={goToNextMonth}
                        >
                            &raquo;
                        </button>
                    </div>

                    {/* Day names header */}
                    <div className="row row-cols-7 g-1 text-center small mb-1">
                        {dayNames.map((d) => (
                            <div key={d} className="col">
                                {d}
                            </div>
                        ))}
                    </div>

                    {/* Day numbers */}
                    <div className="row text-center small mb-3" style={{ gridTemplateColumns: 'repeat(7, 1fr)', display: 'grid' }}>
                        {calendarDays.map((day, idx) => {
                            if (!day) {
                                // blank cell to keep grid aligned
                                return <div key={idx} className="col" ></div>;
                            }
                            const isSelected =
                                day === selectedDay;
                            const isToday =
                                day === today.getDate() &&
                                selectedMonth === today.getMonth() &&
                                selectedYear === today.getFullYear();

                            let btnClass = "btn btn-sm fs-7 w-100 mb-1";
                            if (isSelected) {
                                btnClass += " btn-primary";
                            } else if (isToday) {
                                btnClass += " btn-lemon text-dark";
                            } else {
                                btnClass += " btn-light";
                            }

                            return (
                                <div key={idx} className="col">
                                    <button
                                        type="button"
                                        className={btnClass}
                                        onClick={() => handleDayClick(day)}
                                    >
                                        {day}
                                    </button>
                                </div>
                            );
                        })}
                    </div>



                    {/* Footer buttons */}
                    <div className="d-flex justify-content-between mt-2">
                        <button
                            type="button"
                            className="btn btn-sm btn-danger w-50 me-1"
                            onClick={resetDate}
                        >
                            Clear
                        </button>
                        <button
                            type="button"
                            className="btn btn-sm btn-primary w-50 ms-1"
                            onClick={setToday}
                        >
                            Today
                        </button>
                    </div>
                </div>
            )}

            {/* YEAR VIEW */}
            {isOpen && view === "Year" && (
                <div
                    className="bg-white border rounded shadow-sm mt-1 p-2"
                    style={popupStyle}
                >
                    <div
                        className="overflow-auto"
                        style={{ maxHeight: "220px" }}
                    >
                        <div className="row row-cols-3 g-1 text-center">
                            {possibleYears.map((year) => {
                                const isCurrent = year === selectedYear;
                                return (
                                    <div key={year} className="col">
                                        <button
                                            type="button"
                                            ref={isCurrent ? selectedYearRef : null}
                                            className={`btn btn-sm w-100 ${isCurrent
                                                ? "btn-primary"
                                                : "btn-outline-secondary"
                                                }`}
                                            onClick={() => selectYear(year)}
                                        >
                                            {year}
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}

            {/* MONTH VIEW */}
            {isOpen && view === "Month" && (
                <div
                    className="bg-white border rounded shadow-sm mt-1 p-2"
                    style={popupStyle}
                >
                    <div className="row row-cols-2 g-1 text-center">
                        {monthNames.map((m, idx) => {
                            const isCurrent = idx === selectedMonth;
                            return (
                                <div key={m} className="col">
                                    <button
                                        type="button"
                                        className={`btn btn-sm w-100 ${isCurrent
                                            ? "btn-primary"
                                            : "btn-outline-secondary"
                                            }`}
                                        onClick={() => selectMonth(idx)}
                                    >
                                        {m}
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* If you ever want an Instructions view again, it can be added here,
                also using Bootstrap only. */}
        </div>
    );
};

export default FBXCalendar;
